import { Editor, Range } from '@tiptap/react'
import {
  useState,
  useEffect,
  useImperativeHandle,
  ForwardedRef,
  useMemo,
  useRef,
} from 'react'
import { cn } from '@/utils'
import { Button } from '@/components/ui/button'
import type {
  CommandOption,
  CommandItem,
} from '../SlashCommands/commands/types'

interface SlashCommandListItemsProps {
  parentRef: ForwardedRef<any>
  items: CommandItem[]
  canMultiSelect: boolean
  query: string
  editor: Editor
  range: Range
  updateBomOnSelect: boolean
  alwaysShowEmptyResults?: boolean
  onSelect: (items: CommandItem[]) => void
  renderEmptyResults?: CommandOption['renderEmptyResults']
}

const isSameCommandItem = (a: any, b: any) => {
  if (a === b) return true
  if (typeof a === 'string' || typeof b === 'string') {
    return a === b
  }
  return a?.key === b?.key && a?.value === b?.value && a?.toolId === b?.toolId
}

const isSameList = (a: any[], b: any[]) =>
  a.length === b.length &&
  a.every((item, index) => isSameCommandItem(item, b[index]))

export const CommandSelector = ({
  parentRef,
  items,
  canMultiSelect,
  query,
  editor,
  range,
  updateBomOnSelect,
  alwaysShowEmptyResults,
  onSelect,
  renderEmptyResults = () => <div>No result</div>,
}: SlashCommandListItemsProps) => {
  const listRef = useRef<HTMLDivElement>(null)

  const [selectedItems, setSelectedItems] = useState<
    {
      key: string
      value: string
    }[]
  >([])

  const [filteredItems, setFilteredItems] = useState<any[]>([...items])

  const cleanItems = useMemo(
    () =>
      items.filter((item) =>
        item?.value?.toLowerCase().includes(query?.toLowerCase()),
      ),
    [items, query],
  )

  const isQueryIncludedInItems = useMemo(
    () =>
      cleanItems.some(
        (item) => item?.value?.toLowerCase() === query?.toLowerCase(),
      ),
    [cleanItems, query],
  )

  useImperativeHandle(parentRef, () => ({
    onBlur: () => {
      if (typeof onSelect === 'function') {
        onSelect(selectedItems)
      }
    },
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp' && selectedItems.length > 0) {
        const index = filteredItems.findIndex(
          (item) => item.key === selectedItems[0].key,
        )
        const nextIndex =
          (index + filteredItems.length - 1) % filteredItems.length
        setSelectedItems([filteredItems[nextIndex]])
        return true
      }

      if (event.key === 'ArrowDown' && selectedItems.length > 0) {
        const index = filteredItems.findIndex(
          (item) => item.key === selectedItems[0].key,
        )
        const nextIndex = (index + 1) % filteredItems.length
        setSelectedItems([filteredItems[nextIndex]])
        return true
      }

      if (
        event.key === 'Enter' ||
        event.key === 'Tab' ||
        event.key === 'Escape'
      ) {
        if (typeof onSelect === 'function') {
          onSelect(selectedItems)
        }
        return true
      }
      return false
    },
  }))

  useEffect(() => {
    const hasSelectedItem = selectedItems.some((selectedItem) => {
      return filteredItems.find((item) => item?.key === selectedItem?.key)
    })
    if (alwaysShowEmptyResults && query && !isQueryIncludedInItems) {
      const nextSelectedItem =
        filteredItems.length > 1 ? filteredItems[0] : query
      setSelectedItems((prevSelectedItems) =>
        isSameList(prevSelectedItems, [nextSelectedItem])
          ? prevSelectedItems
          : [nextSelectedItem],
      )
      return
    }
    if (!filteredItems.length) {
      setSelectedItems((prevSelectedItems) =>
        prevSelectedItems.length ? [] : prevSelectedItems,
      )
      return
    }
    if (
      filteredItems.length > 0 &&
      (selectedItems.length === 0 || !hasSelectedItem)
    ) {
      setSelectedItems((prevSelectedItems) =>
        isSameList(prevSelectedItems as any[], [filteredItems[0]])
          ? prevSelectedItems
          : [filteredItems[0]],
      )
    }
  }, [filteredItems, alwaysShowEmptyResults, query, isQueryIncludedInItems])

  useEffect(() => {
    const nextFilteredItems =
      query && alwaysShowEmptyResults && !isQueryIncludedInItems
        ? [...cleanItems, query]
        : cleanItems

    setFilteredItems((previousFilteredItems) =>
      isSameList(previousFilteredItems, nextFilteredItems)
        ? previousFilteredItems
        : nextFilteredItems,
    )
  }, [cleanItems, query, alwaysShowEmptyResults, isQueryIncludedInItems])

  useEffect(() => {
    const selectedElement = listRef.current?.querySelector(
      '[data-selected="true"]',
    )

    selectedElement?.scrollIntoView({ block: 'nearest' })
  }, [selectedItems])

  return (
    <div
      ref={listRef}
      className="flex max-h-[min(320px,calc(100vh-96px))] min-w-[150px] flex-col overflow-y-auto overscroll-contain rounded-md bg-white p-0 text-sm shadow-md"
    >
      {filteredItems.length
        ? filteredItems.map((item, index) =>
            canMultiSelect ? (
              <div
                key={`${item}:${index}-select`}
                className="flex align-items space-x-2 select-none px-2 py-1"
                data-selected={selectedItems.includes(item)}
              >
                <input
                  type="checkbox"
                  id={item.value}
                  onChange={() => {
                    const isSelected =
                      selectedItems.filter((sitem) => sitem === item).length > 0
                    const nextSelectedItems = isSelected
                      ? selectedItems.filter((sitem) => sitem !== item)
                      : [...selectedItems, item]
                    setSelectedItems(nextSelectedItems)
                  }}
                  checked={selectedItems.includes(item)}
                />
                <label htmlFor={item.value}>{item.value}</label>
              </div>
            ) : alwaysShowEmptyResults &&
              item === query &&
              query &&
              !isQueryIncludedInItems ? (
              renderEmptyResults({
                query,
                editor,
                range,
                updateBomOnSelect,
                isSelected: selectedItems.includes(item),
              })
            ) : (
              <button
                key={`${item}:${index}`}
                className={cn(
                  'text-left px-2 py-1 rounded-md hover:bg-slate-300',
                  {
                    'bg-slate-200': selectedItems.includes(item),
                  },
                )}
                data-selected={selectedItems.includes(item)}
                onClick={() => {
                  setSelectedItems([item])
                  if (typeof onSelect === 'function') {
                    onSelect([item])
                  }
                }}
                onMouseDown={(e) => e.stopPropagation()}
                data-testid="slash-command-item-button"
              >
                {item.value}
              </button>
            ),
          )
        : renderEmptyResults({ query, editor, range, updateBomOnSelect })}
      {filteredItems.length > 0 && canMultiSelect && (
        <Button
          size="sm"
          onClick={() => {
            if (typeof onSelect === 'function') {
              onSelect(selectedItems)
            }
          }}
        >
          Done
        </Button>
      )}
    </div>
  )
}
